import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import East from "@mui/icons-material/East";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Box, Divider, type SxProps, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import type { DateTime } from "luxon";
import { type FC, Fragment, type MouseEvent, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useJobColors } from "../../../hooks/useJobColors";
import { useGetResolverJobDisplayName } from "../../../hooks/useJobDisplayName";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useHasRequestOutreach } from "../../../hooks/useRequestOutreach";
import { useRoleGroupColors } from "../../../hooks/useRoleColors";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { getShiftDisplayDate } from "../../../utils/dateUtils";
import { sortedCurveWindows } from "../../../utils/incentive";
import {
	ChangesSource,
	NotificationType,
	type OpenShiftRequest,
	type ReplaceShiftRequest,
	type RequestLedger,
	type ShiftRequest,
	ShiftType,
} from "../../../utils/models";
import { MAX_OPEN_SHIFT_DURATION_HOURS } from "../../../utils/openShiftDuration";
import { requestShiftOptionsRole } from "../../../utils/roles";
import { getRespondedNurseIds, useMobile } from "../../../utils/utilFunctions";
import { useNurseWarnings, WarningDialog } from "../../../utils/warningDialogs/WarningDialog";
import { EditTimePopover } from "../../EditTimePopover";
import { IncentiveCurveBreakdown } from "../../IncentiveCurveBreakdown/IncentiveCurveBreakdown";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { InitiateRoboCallButton } from "../../InitiateRoboCallButton/InitiateRoboCallButton";
import { PostToAgencyControl } from "../../staffing/PostToAgencyControl";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { ThemedSvg } from "../../UIToolkit/ThemedSvg";
import { AgencyPostDelayField } from "../AgencyPostDelayField";
import { useCanPostRequestToAgency } from "../hooks/useCanPostRequestToAgency";
import { FillShiftModal } from "../NotificationsHistory/FillShiftModal";
import { type OutreachCoachMark, RequestOutreachEntry } from "../Outreach/RequestOutreachEntry";
import { useRequestUtils } from "../Provider/useRequestUtils";
import { RequestOriginGlyph } from "../RequestOrigin/RequestOriginGlyph";
import { RequestOriginViewLevel } from "../RequestOrigin/requestOriginMeta";
import { MinimizableManualAddStaff } from "./MinimizableManualAddStaff";

type RequestItemProps = {
	request: ShiftRequest;
	onDelete: (event: MouseEvent<HTMLElement>, request: ShiftRequest) => void;
	onBroadcast: (event: MouseEvent<HTMLElement>, request: ShiftRequest) => void;
	onEditNeededNurses: (event: MouseEvent<HTMLElement>, request: OpenShiftRequest) => void;
	onShowAll: (request: ShiftRequest) => void;
	onTimeChange?: (request: ShiftRequest, newStartTime: DateTime, newEndTime: DateTime, source: ChangesSource) => Promise<void>;
	hasCallOff?: boolean;
	highlightedRequestId?: string | null;
	/** Why the request exists; `null` where the department does not get origins. */
	origins: RequestLedger | null;
	/** The "Who we're asking" first-open highlight, on the one card that carries it. */
	// DEFAULT: absent = no highlight, for callers that don't show the coach mark.
	outreachCoachMark?: OutreachCoachMark;
};

const smallButtonSx = { fontSize: "12px", height: "28px" } as const satisfies SxProps;

export const RequestItem: FC<RequestItemProps> = memo(
	({ request, onDelete, onBroadcast, onEditNeededNurses, onShowAll, onTimeChange, hasCallOff, highlightedRequestId, origins, outreachCoachMark = null }) => {
		const roleGroupColor = useRoleGroupColors();
		const theme = useTheme();
		const navigate = useNavigate();
		const { track } = useAnalytics();
		const resolveJobDisplayName = useGetResolverJobDisplayName();
		const jobColor = useJobColors();
		const { chosenDepartment } = useChosenDepartment();
		const { roster, getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
		const { approveRequest } = useRequestUtils();
		const { t } = useTranslation();
		const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
		const isPickupByLocation = useFeatureFlag(FEATURE_FLAGS.PickupByLocation);
		const { isMobile } = useMobile();
		// Demo departments get "Who we're asking" on every card in place of the call-off bell (APP-933).
		const hasOutreach = useHasRequestOutreach();
		// Multi-window decay curve to break down in the bonus tooltip; null for flat/zero bonuses.
		const curveWindows = sortedCurveWindows(request.incentive_windows);
		const [manualAddPopupOpen, setManualAddPopupOpen] = useState<boolean>(false);
		const [showAgentHistory, setShowAgentHistory] = useState(false);
		const [pendingApprovalData, setPendingApprovalData] = useState<{
			request: ShiftRequest;
			nurseId: number;
		} | null>(null);

		const [anchorRef, setAnchorRef] = useState<HTMLElement | null>(null);
		const [isEditingTime, setIsEditingTime] = useState(false);
		const { roleGroups } = useCanPostRequestToAgency(request);
		const toggleManualAddPopup = useCallback((e?: MouseEvent) => {
			e?.stopPropagation();
			setManualAddPopupOpen((prev) => !prev);
		}, []);

		const handleMobileManualAdd = useCallback(
			(event: MouseEvent<HTMLElement>) => {
				event.stopPropagation();
				track("mobile_manual_add_staff_clicked", {
					request_id: request.id,
					department_id: chosenDepartment.department_id,
				});
				const dateString = getShiftDisplayDate(request.message.shift_start, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).toFormat(
					"yyyy-MM-dd",
				);
				navigate(`/${chosenDepartment.department_id}/add-nurse-to-shift/${dateString}?requestId=${request.id}`);
			},
			[
				track,
				navigate,
				request.id,
				request.message.shift_start,
				chosenDepartment.department_id,
				chosenDepartment.dayStart,
				chosenDepartment.scheduled_shift_delta_minutes,
			],
		);

		const [warningState, _, handleAgencyConfirm, handleOvertimeConfirm, handleDailyHoursConfirm, handleCancelWarnings] = useNurseWarnings(async () => {
			if (pendingApprovalData) {
				await approveRequest(pendingApprovalData.request, pendingApprovalData.nurseId);
				setPendingApprovalData(null);
			}
		}, chosenDepartment.longShiftDuration);

		const approvedNurses = useMemo<number[]>(() => getRespondedNurseIds(request), [request]);

		const openerNurse = useMemo(() => {
			if (request.request_type === NotificationType.Swap) {
				return roster?.find((rosterNurse) => rosterNurse.userId === request.created_by);
			}
			return null;
		}, [request.request_type, request.created_by, roster]);

		const jobName = useMemo(() => {
			if (!jobManagementEnabled || !request.job_id) return null;
			return resolveJobDisplayName(request.job_id) ?? null;
		}, [jobManagementEnabled, request.job_id, resolveJobDisplayName]);

		const isHighlighted = highlightedRequestId === request.id;

		return (
			<Fragment>
				<Box
					data-testid={`request-card-${request.id}`}
					component={motion.div}
					onClick={() => (isMobile || !!approvedNurses.length) && onShowAll(request)}
					whileHover={{
						scale: 1.02,
						boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
						transition: { duration: 0.2 },
					}}
					sx={{
						mx: 0.45,
						borderRadius: "8px",
						p: 1.5,
						color: "primary.dark",
						border: isHighlighted ? (theme) => `2px solid ${theme.palette.primary.main}` : "1px solid #E3E4F0",
						boxShadow: isHighlighted
							? (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}, 0 4px 12px ${alpha(theme.palette.primary.main, 0.16)}`
							: "none",
						cursor: approvedNurses.length ? "pointer" : "default",
						transition: "border 0.5s ease, box-shadow 0.5s ease",
					}}
				>
					<Box
						data-testid={`request-header-${request.id}`}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 1,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, minWidth: 0, maxWidth: "100%" }}>
							{request.request_type === NotificationType.ReplaceShift || request.request_type === NotificationType.ReplaceAgency ? (
								<>
									<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
										{request.request_type === NotificationType.ReplaceAgency ? t("replace.agency") : t("replace.request")}
									</Typography>
									<SwapHorizIcon sx={{ fontSize: 20 }} />
									<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
										{getNurseFormattedName((request as ReplaceShiftRequest).replacement_nurse_id)}
									</Typography>
								</>
							) : request.request_type === NotificationType.OpenShift ? (
								<>
									<East sx={{ fontSize: 20 }} />
									<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
										{t("open.shift")}
									</Typography>
								</>
							) : request.request_type === NotificationType.Swap ? (
								<>
									<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
										{t("swap.request")}
									</Typography>
									<SwapHorizIcon sx={{ fontSize: 20 }} />
									{openerNurse && (
										<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
											{getNurseFormattedName(openerNurse.id)}
										</Typography>
									)}
								</>
							) : null}
							{request.charge_only && (
								<InHouseTooltip title={t("charge.only")}>
									<ThemedSvg src="/charge.svg" alt="Charge Only" size={20} color="var(--inhouse-primary)" sx={{ ml: 1 }} />
								</InHouseTooltip>
							)}
							{!!(request.current_bonus ?? request.bonus) && (
								<InHouseTooltip
									title={
										curveWindows ? (
											<IncentiveCurveBreakdown windows={curveWindows} postedAt={request.created_at} hoursLeft={request.hours_left} />
										) : (
											`$${request.current_bonus ?? request.bonus} ${t("pickup.bonus")}`
										)
									}
								>
									<img src="/bonus.svg" alt="Bonus" style={{ marginLeft: 8 }} />
								</InHouseTooltip>
							)}
							<PostToAgencyControl request={request} variant="chip" />
							{origins !== null && <RequestOriginGlyph request={request} ledger={origins} level={RequestOriginViewLevel.List} sheetBottomOffset={undefined} />}
						</Box>
						<Box
							data-testid={`request-actions-${request.id}`}
							sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", marginInlineStart: "auto", maxWidth: "100%" }}
						>
							<InitiateRoboCallButton requestId={request.id} iconSizeOverride={"small"} date={request.message.shift_start} />
							{isMobile && request.request_type === NotificationType.OpenShift && (
								<SystemButton
									data-testid={`request-manual-add-${request.id}`}
									leadingIcon={<AddIcon fontSize={"small"} />}
									tooltip="Manual Add Staff"
									iconButton
									onClick={handleMobileManualAdd}
								/>
							)}
							{request.request_type === NotificationType.OpenShift && (
								<SystemButton
									data-testid={`request-edit-time-${request.id}`}
									leadingIcon={<AccessTimeIcon fontSize={"small"} />}
									tooltip="Edit shift time"
									iconButton
									onClick={(event) => {
										event.stopPropagation();
										setAnchorRef(event.currentTarget);
										setIsEditingTime(true);
									}}
								/>
							)}

							<PostToAgencyControl request={request} variant="button" />

							{!isMobile && (
								<SystemButton
									data-testid={`request-broadcast-${request.id}`}
									leadingIcon={<ChatOutlinedIcon fontSize={"small"} />}
									tooltip="Message relevant staff (that have not accepted yet)"
									iconButton
									onClick={(event) => onBroadcast(event, request)}
								/>
							)}
							{!isMobile && (
								<SystemButton
									data-testid={`request-manual-add-${request.id}`}
									leadingIcon={<AddIcon fontSize={"small"} />}
									tooltip="Manual Add Staff"
									iconButton
									onClick={toggleManualAddPopup}
								/>
							)}
							<SystemButton
								data-testid={`request-delete-${request.id}`}
								leadingIcon={<DeleteOutlined fontSize={"small"} />}
								tooltip="Delete Request"
								iconButton
								sx={{ "&:hover": { backgroundColor: "hsla(7, 82%, 39%, 0.10)" } }}
								onClick={(event) => onDelete(event, request)}
							/>
							{!isMobile && hasOutreach && <RequestOutreachEntry request={request} coachMark={outreachCoachMark} />}
							{!isMobile && !hasOutreach && hasCallOff && (
								<SystemButton
									data-testid={`request-notifications-history-${request.id}`}
									leadingIcon={<NotificationsOutlinedIcon fontSize={"small"} />}
									tooltip="View Notifications History"
									iconButton
									sx={{ "&:hover": { backgroundColor: "hsla(7, 82%, 39%, 0.10)" } }}
									onClick={(e) => {
										e.stopPropagation();
										setShowAgentHistory(true);
									}}
								/>
							)}
						</Box>
					</Box>
					<Box sx={{ display: "flex", justifyContent: "space-between" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
							{jobManagementEnabled && jobName && request.job_id ? (
								<Typography sx={{ fontWeight: 600, color: jobColor(request.job_id).ink }}>
									{jobName}
									{request.sub_role ? ` · ${request.sub_role}` : ""}
								</Typography>
							) : (
								roleGroups.length > 0 &&
								roleGroups.map((roleGroupName) => (
									<Typography
										key={roleGroupName}
										sx={{
											fontWeight: 600,
											color: roleGroupColor(roleGroupName, chosenDepartment.role_groups).ink,
										}}
									>
										{roleGroupName.charAt(0).toUpperCase() + roleGroupName.slice(1)}
									</Typography>
								))
							)}
						</Box>
						{request.request_type === NotificationType.OpenShift && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<SystemButton
									data-testid={`request-edit-needed-nurses-${request.id}`}
									tooltip="Edit number of staff needed"
									onClick={(event: MouseEvent<HTMLElement>) => onEditNeededNurses(event, request as OpenShiftRequest)}
									leadingIcon={<ThemedSvg src="/staff.svg" alt="staff" size={20} color="var(--inhouse-primary)" />}
									sx={{
										bgcolor: "transparent",
										color: "primary.dark",
										"&:hover": { bgcolor: "#f5f5f5" },
									}}
								>
									{(request as OpenShiftRequest).needed_nurses}
								</SystemButton>
							</Box>
						)}
					</Box>
					{isPickupByLocation && request.location && (
						<Typography sx={{ fontWeight: 600, fontSize: "14px", color: (theme) => alpha(theme.palette.primary.dark, 0.5) }}>{request.location}</Typography>
					)}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							pb: 1,
							justifyContent: "space-between",
							whiteSpace: "nowrap",
							overflow: "hidden",
						}}
					>
						<Box sx={{ display: "flex", gap: 0.5 }}>
							<Typography sx={{ fontWeight: 600, fontSize: "14px", color: (theme) => alpha(theme.palette.primary.dark, 0.5) }}>
								{getShiftDisplayDate(request.message.shift_start, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).toFormat(
									"ccc, MMM d",
								)}
							</Typography>
							{request.message.shift_type === ShiftType.Day ? <img src="/sun.svg" alt="Day shift" /> : <img src="/moon.svg" alt="Night shift" />}
							<Typography sx={{ fontWeight: 600, fontSize: "14px", color: (theme) => alpha(theme.palette.primary.dark, 0.5) }}>
								{request.message.shift_start.toFormat("h:mm a")}-{request.message.shift_end.toFormat("h:mm a")}
							</Typography>
						</Box>
					</Box>
					{approvedNurses.length > 0 && (
						<>
							<Divider />
							<Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
								<SystemButton data-testid={`request-show-all-${request.id}`} sx={smallButtonSx} onClick={() => onShowAll(request)}>
									{t("show.all")} ({approvedNurses.length})
								</SystemButton>
							</Box>
						</>
					)}
				</Box>
				{!isMobile && manualAddPopupOpen && (
					<MinimizableManualAddStaff open={manualAddPopupOpen} onClose={toggleManualAddPopup} approvedNursesIds={approvedNurses} request={request} />
				)}
				<WarningDialog
					openAgencyWarning={warningState.isAgencyWarningOpen}
					openOvertimeWarning={warningState.isOvertimeWarningOpen}
					openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
					overtimeDetails={warningState.overtimeDetails}
					dailyHoursDetails={warningState.dailyHoursDetails}
					longShiftDuration={chosenDepartment.longShiftDuration}
					onCancelAgency={handleCancelWarnings}
					onConfirmAgency={handleAgencyConfirm}
					onCancelOvertime={handleCancelWarnings}
					onConfirmOvertime={handleOvertimeConfirm}
					onCancelDailyHours={handleCancelWarnings}
					onConfirmDailyHours={handleDailyHoursConfirm}
				/>
				{isEditingTime && anchorRef && (
					<EditTimePopover
						role={requestShiftOptionsRole(request.message.roles)}
						jobId={request.job_id}
						startTime={request.message.shift_start}
						endTime={request.message.shift_end}
						onTimeChange={({ newStartTime, newEndTime }) => {
							onTimeChange?.(request, newStartTime, newEndTime, ChangesSource.Requests);
							setIsEditingTime(false);
						}}
						onClose={() => setIsEditingTime(false)}
						anchorEl={anchorRef}
						isActual={false}
						extraContent={<AgencyPostDelayField request={request as OpenShiftRequest} />}
						maximumDurationHours={MAX_OPEN_SHIFT_DURATION_HOURS}
					/>
				)}
				{!isMobile && showAgentHistory && (
					<Box
						sx={{
							border: "1px dashed #E3E4F0",
							borderRadius: "8px",
							m: 2,
						}}
					>
						<FillShiftModal
							open={showAgentHistory}
							onClose={() => setShowAgentHistory(false)}
							requestId={request.id}
							shift_start={request.message.shift_start}
							shift_end={request.message.shift_end}
						/>
					</Box>
				)}
			</Fragment>
		);
	},
);
